import React, { useEffect, useRef } from "react"
import moment from "moment-timezone"
import { Link } from "react-router-dom"
import { categoryLabel, clubLabel, competitionsLabel, daysLabel, lastDateChangeText, playModeLabel, sportLabel, stateLabel, weekdayTimeLabel }
  from "./plannerLabels"
import { movable, openWarnings } from "./plannerItems"
import { eventLockText } from "./proposalMessages"
import { stateLines } from "./ItemState"
import WarningRow from "./WarningRow"

// What the quick peek says of a published event's dates (ticket 14, Phase E3): until when the calendar moves it, or that its page changes
// them now; its last date change; and the event's History (Event details).
function EventDates({ item, timeZone }) {
  const zone = item.timezone || timeZone
  return <>
    <p className="ep-banner ep-banner-info mb-0">{movable(item) ? `Movable until registrations open on ${weekdayTimeLabel(item.registrationsOpenAt, zone)}.`
      : <><i className="bx bx-lock-alt" aria-hidden="true" /> {eventLockText(item)}</>}</p>
    <p className="ep-meta mb-0">{item.lastDateChange && `${lastDateChangeText(item.lastDateChange, item.days, zone)} `}
      <Link to={`/events/${item.id}`}>History</Link></p>
  </>
}

// "Dates changed after it was sent (was Sat 7 – Sun 8 Nov)": the dates the organizer was sent (Phase C2a marks), as days of the country.
const sentDays = (changes, item, timeZone) => {
  const start = changes.find(({ field }) => field === "startDate"), end = changes.find(({ field }) => field === "endDate")
  if (!start && !end) return null
  const dayOf = (change, fallback) => (change?.sent ? moment(change.sent).tz(timeZone).format("YYYY-MM-DD") : fallback)
  return { start: dayOf(start, item.days.start), end: dayOf(end, item.days.end) }
}

// The quick peek (ticket 15, ticket 13 Q11): a click on a card or a chip shows the tournament's facts, its warnings with their actions
// and what can be done with it (`stateActions`; for an event published from a proposal, its proposal), beside the calendar. Esc or Close
// hides it; Edit opens the proposal in the editor.
export default function PlannerPeek({ item, settings, timeZone, onClose, onEdit, stateActions, warningActions, moves }) {
  const heading = useRef(null)
  useEffect(() => { heading.current?.focus() }, [item.key])
  useEffect(() => {
    const keys = event => { if (event.key === "Escape" && !event.defaultPrevented) onClose() }
    document.addEventListener("keydown", keys)
    return () => document.removeEventListener("keydown", keys)
  }, [onClose])

  const proposal = item.kind === "proposal"
  const warnings = item.warnings || []
  const open = openWarnings(item).length
  const changed = item.marks?.changedAfterSent || []
  const was = sentDays(changed, item, timeZone)
  const answer = stateLines(item, timeZone).map(({ text }) => text).join(" · ")
  return <aside className="ep-peek" aria-label={`${item.label} quick peek`}>
    <header className="ep-peek-head">
      <div className="ep-drawer-bar">
        <span className={`ep-state ep-state-${item.state}`}>{stateLabel(item.state)}</span>
        <span><span className={`ep-dot ep-cat-${item.category}`} aria-hidden="true" />{categoryLabel(settings, item.category)}</span>
        <span className="ep-spacer" />
        <button type="button" className="ep-icon-button" aria-label="Close the quick peek" onClick={onClose}><i className="bx bx-x" aria-hidden="true" /></button>
      </div>
      <h2 ref={heading} tabIndex={-1}>{`${item.label} · ${item.club?.city || clubLabel(item.club)}`}</h2>
      <span className="ep-meta">{`${item.name || "No name yet"} · Circuit: Sportya`}</span>
      <span className="ep-peek-marks">
        {item.marks?.needsNewConfirmation && <span className="ep-mark ep-mark-warn">Needs a new confirmation</span>}
        {changed.length > 0 && <span className="ep-mark ep-mark-soon">{was ? `Dates changed after it was sent (was ${daysLabel(was)})` : "Changed after it was sent"}</span>}
        {item.needsFees && <span className="ep-mark ep-mark-error">No fees</span>}
        {item.cancelled && <span className="ep-mark ep-mark-warn">Cancelled</span>}
      </span>
    </header>
    <div className="ep-peek-body">
      <dl className="ep-facts">
        <dt>Dates</dt><dd>{daysLabel(item.days)}</dd>
        <dt>Club</dt><dd>{clubLabel(item.club)}</dd>
        <dt>Region</dt><dd>{item.region || item.county || "—"}</dd>
        <dt>Organizer</dt><dd>{item.organizer ? item.organizer.companyName || "Organizer without a company name" : "No organizer yet"}</dd>
        <dt>Format</dt><dd>{`${playModeLabel(item.playMode) || "No play mode yet"} · ${sportLabel(item.sportType)}`}</dd>
        <dt>Competitions</dt><dd className="ep-tiles">{competitionsLabel(item.competitions) || "None yet"}</dd>
        {proposal && <><dt>Answer</dt><dd>{answer || "—"}</dd></>}
        {item.previousEdition && <><dt>Previous</dt><dd>{`Event ${item.previousEdition.eventId}`}</dd></>}
      </dl>
      {warnings.length > 0 && <section className="ep-stack ep-gap" aria-label="Warnings">
        <span className="ep-peek-row"><span className="ep-label">Warnings</span>
          <span className="ep-meta">{`${open} open${warnings.length > open ? ` · ${warnings.length - open} acknowledged` : ""}`}</span></span>
        {warnings.map(warning => <WarningRow key={warning.key} warning={warning} timeZone={timeZone} actions={warningActions} />)}
      </section>}
      {!proposal && <EventDates item={item} timeZone={timeZone} />}
      {proposal && ["refused", "withdrawn"].includes(item.state) && <p className="ep-banner ep-banner-info mb-0">Reopen it as a draft to change it.</p>}
      {moves}
    </div>
    <footer className="ep-peek-foot">
      {proposal ? <button type="button" className="btn btn-light" onClick={() => onEdit(item)}>Edit</button>
        : <Link className="btn btn-light" to={`/events/${item.id}`}>Open event #{item.eventId}</Link>}
      {stateActions}
    </footer>
  </aside>
}
